'use client'

import Link from 'next/link'
import { ShoppingCartIcon } from '@heroicons/react/24/outline'
import { Button } from '@/General/Button'
import { AddedToCartModal } from './AddedToCartModal'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { ChooseOptionModal } from './ChooseOptionModal'
import { AlreadyPurchasedModal } from './AlreadyPurchasedModal'
import { useStore } from '@/store'
import {
  useHasPurchasedProductQuery,
  useSubscriptionEligibilityQuery,
} from '@/generated/graphql'
import { useQueryClient } from '@tanstack/react-query'
import { EmailNotificationModal } from '@/EmailNotificationModal'
import { LoginModal } from '@/MainLayout/Header/TopMenu/LoginModal'
import { MasterclassButtons } from './MasterclassButtons'
import { toastEmitter } from '@/General/Toast'
import { useAddItemToCart } from '@/useAddItemToCart'
import {
  MASTERCLASS_TRIAL_PRODUCT_ID,
  MASTERCLASS_SUBSCRIPTION_PRODUCT_ID,
} from '@/productIds'
import { tracking } from '@/tracking'
import { EBOOK_BUNDLE_SENTINEL } from '@/ebookBundle'
import { getFlowSlugForProductId } from '@/lib/config/customAddToCartFlows'
import {
  analyzeGroupChildCart,
  firstPurchasableChildOptionId,
  formatPartialGroupCartNotice,
  shouldOfferPartialGroupChildAdd,
  type GroupChildProductForCart,
} from '@/lib/groupChildCart'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'
import { effectiveOptionPrice } from '@/utils/tracking/effectiveOptionPrice'
import { useCookieValue } from '@/Cookies/useCookies'
import {
  VAT_PREFERENCE_COOKIE,
  getDefaultVATPreference,
  type VATPreference,
} from '@/vat/vatUtils'
import { useStorefrontMarket } from '@/useStorefrontMarket'
import { reportClientError } from '@/lib/logging/clientErrorReporter'
import { isDownloadProductType } from '@/lib/formatProductType'
import { formatAddToCartOptionLabel } from './optionLabel'

const getCountryCodeForBrand = (
  brandCode: RegionalBrandCode | null | undefined
) => {
  if (brandCode === 'VANISHING_INC_UK') return 'GB'
  if (brandCode === 'VANISHING_INC_US') return 'US'
  return null
}

const toReportableError = (error: unknown) =>
  error instanceof Error
    ? error
    : new Error(typeof error === 'string' ? error : 'Unknown add-to-cart error')

const isComponentOption = (option: { productType?: string | null }) =>
  option.productType?.toLowerCase() === 'component'

const isDownloadOnlyOption = (
  option: AddToCartButtonProps['options'][number]
) => isDownloadProductType(option.productType) || !option.needsShipping

type DownloadOnlyPurchaseCheck =
  | { shouldCheck: false }
  | { shouldCheck: true; productOptionId?: number }

const getDownloadOnlyPurchaseCheck = ({
  hasDownloads,
  options,
  productOptionId,
}: {
  hasDownloads: boolean
  options: AddToCartButtonProps['options']
  productOptionId?: number
}): DownloadOnlyPurchaseCheck => {
  if (!hasDownloads) return { shouldCheck: false }

  const purchasableOptions = options.filter(
    option => !isComponentOption(option)
  )

  if (productOptionId != null) {
    const selectedOption = purchasableOptions.find(
      option => option.id === productOptionId
    )
    if (!selectedOption || !isDownloadOnlyOption(selectedOption)) {
      return { shouldCheck: false }
    }

    const wholeProductIsDownloadOnly =
      purchasableOptions.every(isDownloadOnlyOption)

    return {
      shouldCheck: true,
      productOptionId: wholeProductIsDownloadOnly ? undefined : productOptionId,
    }
  }

  if (purchasableOptions.length === 0) return { shouldCheck: true }

  return {
    shouldCheck: purchasableOptions.every(isDownloadOnlyOption),
  }
}

const AddToCartButtonInner = ({
  productId,
  productName,
  className,
  buttonSize = 'lg',
  options,
  productInStock = false,
  children,
  showOptions = false,
  showEmailNotification = false,
  emailNotificationDisplay = 'button',
  isMasterclass = false,
  isDeckOfCards = false,
  hasDownloads = false,
  ebookBundleOption = null,
  isPreorder = false,
  preorderReleaseDate = null,
  groupChildProducts,
  independentOptionSelection = false,
  brandCode = null,
}: Omit<AddToCartButtonProps, 'configureFlowLinkClassName'>) => {
  const cookieVatPreference = useCookieValue(
    VAT_PREFERENCE_COOKIE
  ) as VATPreference
  const defaultCurrency = useStorefrontMarket({
    fallbackBrand: brandCode,
    currencyCode: options[0]?.currencyCode,
    currencySymbol: options[0]?.currencySymbol,
  })
  const displayBrand = defaultCurrency.brandCode
  const storefrontCurrencySymbol = defaultCurrency.currencySymbol
  const storefrontCurrencyCode = defaultCurrency.currencyCode
  const countryCode =
    defaultCurrency.countryIsoCode ||
    getCountryCodeForBrand(displayBrand) ||
    'US'
  const vatPreference =
    cookieVatPreference || getDefaultVATPreference(countryCode)
  const [isAddedToCartModalOpen, setIsAddedToCartModalOpen] = useState(false)
  const [cartNotice, setCartNotice] = useState<string | null>(null)
  const [addedProductName, setAddedProductName] = useState(productName)
  const [isChooseOptionModalOpen, setIsChooseOptionModalOpen] = useState(false)
  const [isAlreadyPurchasedModalOpen, setIsAlreadyPurchasedModalOpen] =
    useState(false)
  const [isEmailNotificationOpen, setIsEmailNotificationOpen] = useState(false)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const [selectedQuantity, setSelectedQuantity] = useState(1)
  const storeSelectedOptionId = useStore(state => state.selectedOptionId)
  const setStoreSelectedOptionId = useStore(state => state.setSelectedOptionId)
  const [selectedOptionId, setLocalSelectedOptionId] = useState<number | null>(
    () => (independentOptionSelection ? null : storeSelectedOptionId)
  )
  const previousOptionsRef = useRef<typeof options | null>(null)
  const nonComponentOptions = useMemo(
    () => options.filter(option => !isComponentOption(option)),
    [options]
  )
  const shouldShowOptionsInModal = useMemo(() => {
    if (nonComponentOptions.length === 0) return false
    return nonComponentOptions.some(
      option => option.description.trim().length > 0
    )
  }, [nonComponentOptions])
  const singleOptionId = useMemo(() => {
    if (nonComponentOptions.length !== 1) return null
    return nonComponentOptions[0]?.id ?? null
  }, [nonComponentOptions])
  const deckFallbackOptionId = useMemo(() => {
    const firstInStockNonComponent = options.find(
      option =>
        option.inStock &&
        option.productType?.toLowerCase() !== 'component' &&
        option.id != null
    )
    if (firstInStockNonComponent != null) return firstInStockNonComponent.id
    if (isPreorder) {
      const firstNonComponent = options.find(
        option =>
          option.productType?.toLowerCase() !== 'component' && option.id != null
      )
      return firstNonComponent?.id ?? null
    }
    const firstNonComponent = options.find(
      option =>
        option.productType?.toLowerCase() !== 'component' && option.id != null
    )
    return firstNonComponent?.id ?? null
  }, [options, isPreorder])

  // Compute the default option to auto-select
  const defaultOptionId = useMemo(() => {
    if (options.length <= 1) return null
    const firstInStockOption = options.find(
      option =>
        option.inStock &&
        option.id != null &&
        option.productType?.toLowerCase() !== 'component'
    )
    if (firstInStockOption != null) return firstInStockOption.id
    if (isPreorder) {
      const first = options.find(
        option =>
          option.id != null && option.productType?.toLowerCase() !== 'component'
      )
      return first?.id ?? null
    }
    const firstNonComponent = options.find(
      option =>
        option.id != null && option.productType?.toLowerCase() !== 'component'
    )
    return firstNonComponent?.id ?? null
  }, [options, isPreorder])

  // Update store when local state changes (parent PDP); child rows keep selection local only.
  const handleOptionIdChange = useCallback(
    (optionId: number | null) => {
      setLocalSelectedOptionId(optionId)
      if (!independentOptionSelection) {
        setStoreSelectedOptionId(optionId)
      }
    },
    [independentOptionSelection, setStoreSelectedOptionId]
  )

  const closeChooseOptionModal = useCallback(
    ({
      resetOptionSelection = true,
    }: { resetOptionSelection?: boolean } = {}) => {
      setIsChooseOptionModalOpen(false)
      if (
        resetOptionSelection &&
        (!isDeckOfCards || shouldShowOptionsInModal)
      ) {
        handleOptionIdChange(null)
      }
      setSelectedQuantity(1)
    },
    [
      handleOptionIdChange,
      isDeckOfCards,
      setIsChooseOptionModalOpen,
      setSelectedQuantity,
      shouldShowOptionsInModal,
    ]
  )

  // Auto-select default option when options first load/change and no option is selected.
  useEffect(() => {
    const optionsChanged = previousOptionsRef.current !== options
    previousOptionsRef.current = options

    if (
      optionsChanged &&
      options.length > 1 &&
      selectedOptionId == null &&
      defaultOptionId != null
    ) {
      // Defer state update to avoid setState in effect warning
      const timeoutId = setTimeout(() => {
        handleOptionIdChange(defaultOptionId)
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [options, selectedOptionId, defaultOptionId, handleOptionIdChange])

  useEffect(() => {
    if (independentOptionSelection) return
    if (options.length === 0 || storeSelectedOptionId == null) return
    if (!options.some(o => o.id === storeSelectedOptionId)) {
      const timeoutId = setTimeout(() => {
        handleOptionIdChange(defaultOptionId)
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [
    independentOptionSelection,
    productId,
    options,
    storeSelectedOptionId,
    defaultOptionId,
    handleOptionIdChange,
  ])

  // Check stock status
  const allOptionsOutOfStock =
    options.length > 0 && options.every(option => !option.inStock)
  const effectiveAllOutOfStock = allOptionsOutOfStock && !isPreorder
  const noOptionsAvailable = options.length === 0 && !productInStock

  const offersPartialGroupChildAdd = useMemo(
    () =>
      shouldOfferPartialGroupChildAdd({
        groupChildProducts,
        parentIsPreorder: isPreorder,
        parentOptions: options,
      }),
    [groupChildProducts, isPreorder, options]
  )

  const user = useStore(state => state.user)
  const hydrated = useStore(state => state.hydrated)
  const queryClient = useQueryClient()

  const { addItem: addItemToCart, isPending } = useAddItemToCart({
    onSuccess: ({ productId: addedProductId, productOptionId, quantity }) => {
      setCartNotice(null)
      closeChooseOptionModal({ resetOptionSelection: false })
      // Set the correct product name for the modal
      const resolvedName =
        addedProductId === MASTERCLASS_TRIAL_PRODUCT_ID
          ? 'Vanishing Inc. Masterclass'
          : productName
      setAddedProductName(resolvedName)

      // Tracking (Kissmetrics primary; GA4 mapped via registry)
      const currency = storefrontCurrencyCode || undefined
      const option =
        productOptionId != null
          ? (options.find(o => o.id === productOptionId) ?? null)
          : options.length === 1
            ? options[0]
            : (options.find(o => o.id === selectedOptionId) ?? null)

      tracking.track('Added item to cart', {
        productId: addedProductId,
        productName: resolvedName,
        // Report the price the customer actually pays (sale price when discounted),
        // not the list price, so GA4 value/revenue isn't inflated on sale items.
        price: effectiveOptionPrice(option),
        quantity: quantity ?? 1,
        currency,
      })

      setIsAddedToCartModalOpen(true)
    },
    onCartCreateError: () => {
      toastEmitter.emit('toast', {
        title: 'Failed to create cart',
        variant: 'error',
      })
    },
    onError: error => {
      console.error('AddToCart error:', error)
      reportClientError(toReportableError(error), 'add_to_cart', {
        productId: String(productId),
        productName,
        selectedOptionId:
          selectedOptionId == null ? undefined : String(selectedOptionId),
        optionCount: String(options.length),
        productInStock: String(productInStock),
        isPreorder: String(isPreorder),
        brandCode: displayBrand ?? undefined,
        currencyCode: storefrontCurrencyCode ?? undefined,
      })
      toastEmitter.emit('toast', {
        title: 'Failed to add item to cart',
        variant: 'error',
      })
    },
  })

  const checkPurchaseAndAddToCart = async (
    targetProductId: number,
    productOptionId?: number,
    quantity: number = 1
  ) => {
    // For masterclass subscription products (trial or regular), check eligibility first
    if (
      targetProductId === MASTERCLASS_TRIAL_PRODUCT_ID ||
      targetProductId === MASTERCLASS_SUBSCRIPTION_PRODUCT_ID
    ) {
      // Only check if user is logged in (can't check eligibility for anonymous users)
      if (user?.id && hydrated) {
        try {
          const eligibilityData = await queryClient.fetchQuery({
            queryKey: ['SubscriptionEligibility'],
            queryFn: () => useSubscriptionEligibilityQuery.fetcher({})(),
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
          })

          // Check if user is already actively subscribed
          if (eligibilityData?.subscriptionEligibility?.isSubscriber) {
            toastEmitter.emit('toast', {
              title: 'Already subscribed',
              body: 'You already have an active subscription. Please manage your subscription from your account page.',
              variant: 'error',
            })
            return
          }

          // For trial products, check trial eligibility
          if (targetProductId === MASTERCLASS_TRIAL_PRODUCT_ID) {
            if (!eligibilityData?.subscriptionEligibility?.isEligibleForTrial) {
              // Not eligible - show error and don't add to cart
              toastEmitter.emit('toast', {
                title: 'Trial not available',
                body: 'You are not eligible for a trial subscription. Please subscribe to the regular plan instead.',
                variant: 'error',
              })
              return
            }
          }
        } catch (error) {
          // If check fails, allow adding to cart (fail open)
          console.error('Failed to check subscription eligibility:', error)
        }
      }

      void addItemToCart({ productId: targetProductId, quantity })
      return
    }

    const previousPurchaseCheck = getDownloadOnlyPurchaseCheck({
      hasDownloads,
      options,
      productOptionId,
    })

    // Only block repeat purchases for download-only products/options.
    if (previousPurchaseCheck.shouldCheck && user?.id && hydrated) {
      try {
        const purchaseData = await queryClient.fetchQuery({
          queryKey: [
            'HasPurchasedProduct',
            {
              productId: targetProductId,
              productOptionId: previousPurchaseCheck.productOptionId,
            },
          ],
          queryFn: () =>
            useHasPurchasedProductQuery.fetcher({
              productId: targetProductId,
              productOptionId: previousPurchaseCheck.productOptionId,
            })(),
          staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        })

        if (purchaseData?.hasPurchasedProduct) {
          setIsAlreadyPurchasedModalOpen(true)
          return
        }
      } catch (error) {
        // If check fails, allow adding to cart (fail open)
        console.error('Failed to check purchase status:', error)
      }
    }

    void addItemToCart({
      productId: targetProductId,
      productOptionId: productOptionId ?? null,
      quantity,
      ebookBundleOption:
        productOptionId === EBOOK_BUNDLE_SENTINEL && ebookBundleOption
          ? {
              physicalOptionId: ebookBundleOption.physicalOptionId,
              downloadOptionId: ebookBundleOption.downloadOptionId,
            }
          : null,
    })
  }

  const handleAddItemToCart = (
    targetProductId: number,
    productOptionId?: number,
    quantity: number = 1
  ) => {
    checkPurchaseAndAddToCart(targetProductId, productOptionId, quantity)
  }

  const handleAddToCart = async () => {
    // Partial group child add: only add children when parent is not fully available
    if (offersPartialGroupChildAdd) {
      const { purchasableChildren, oosChildren } = analyzeGroupChildCart(
        groupChildProducts,
        isPreorder
      )
      setAddedProductName(productName)
      try {
        for (const child of purchasableChildren) {
          const optId = firstPurchasableChildOptionId(child, isPreorder)
          if (optId == null) continue
          await addItemToCart(
            { productId: child.id, productOptionId: optId, quantity: 1 },
            { skipCallbacks: true }
          )
          const opt = child.options?.find(o => o.id === optId)
          const childLabel =
            (child.name && child.name.trim()) || `Product #${child.id}`
          tracking.track('Added item to cart', {
            productId: child.id,
            productName: childLabel,
            price: effectiveOptionPrice(opt),
            quantity: 1,
            currency: storefrontCurrencyCode || undefined,
          })
        }
        setCartNotice(formatPartialGroupCartNotice(oosChildren))
        closeChooseOptionModal({ resetOptionSelection: false })
        setIsAddedToCartModalOpen(true)
      } catch (e) {
        console.error('Partial group add failed:', e)
        toastEmitter.emit('toast', {
          title: 'Could not add all items to your cart',
          variant: 'error',
        })
      }
      return
    }

    // If no options are available, show email notification modal (only if enabled)
    // Exception: Masterclass products (trial and subscription) should always work
    if (
      noOptionsAvailable &&
      productId !== MASTERCLASS_TRIAL_PRODUCT_ID &&
      productId !== MASTERCLASS_SUBSCRIPTION_PRODUCT_ID
    ) {
      if (showEmailNotification) {
        if (!user) {
          setIsLoginOpen(true)
        } else {
          setIsEmailNotificationOpen(true)
        }
        return
      }
      return
    }

    // Check if all options are out of stock (pre-orders are still purchasable)
    if (effectiveAllOutOfStock) {
      // Parent is OOS and no children available, show email notification
      if (showEmailNotification) {
        if (!user) {
          setIsLoginOpen(true)
        } else {
          setIsEmailNotificationOpen(true)
        }
        return
      }
      return
    }

    // If we have multiple options, use selected option or fallback to default
    if (options.length > 1) {
      const optionToUse = selectedOptionId ?? defaultOptionId
      if (optionToUse != null) {
        handleAddItemToCart(productId, optionToUse)
        return
      }
    }

    if (singleOptionId != null) {
      handleAddItemToCart(productId, singleOptionId)
      return
    }

    handleAddItemToCart(productId)
  }

  const handleChooseOption = () => {
    if (!isDeckOfCards || shouldShowOptionsInModal) {
      handleOptionIdChange(null) // Reset selection when opening modal
    }
    setSelectedQuantity(1)
    setIsChooseOptionModalOpen(true)
  }

  const handleOptionSelect = (optionId: number) => {
    handleOptionIdChange(optionId)
  }

  const handleAddToCartWithOption = async () => {
    const quantityToAdd = Math.max(1, selectedQuantity)
    const chosenOptionId = selectedOptionId
    if (
      chosenOptionId != null &&
      options.some(option => option.id === chosenOptionId)
    ) {
      handleAddItemToCart(productId, chosenOptionId, quantityToAdd)
      return
    }

    if (isDeckOfCards && deckFallbackOptionId != null) {
      handleAddItemToCart(productId, deckFallbackOptionId, quantityToAdd)
      return
    }

    if (singleOptionId != null) {
      handleAddItemToCart(productId, singleOptionId, quantityToAdd)
      return
    }

    handleAddItemToCart(productId, undefined, quantityToAdd)
  }

  const handlePurchaseLecture = () => {
    if (noOptionsAvailable || effectiveAllOutOfStock) {
      if (showEmailNotification) {
        if (!user) {
          setIsLoginOpen(true)
        } else {
          setIsEmailNotificationOpen(true)
        }
      }
      return
    }

    // If an option is selected (e.g. from dropdown), purchase that option directly.
    if (options.length > 1 && selectedOptionId != null) {
      handleAddItemToCart(productId, selectedOptionId)
      return
    }

    if (options.length > 1) {
      handleChooseOption()
      return
    }

    if (singleOptionId != null) {
      handleAddItemToCart(productId, singleOptionId)
      return
    }

    handleAddItemToCart(productId)
  }

  // Don't show button if email notification is disabled and product is out of stock
  // Exception: Masterclass products (trial and subscription) should always show
  if (
    !showEmailNotification &&
    (effectiveAllOutOfStock || noOptionsAvailable) &&
    productId !== MASTERCLASS_TRIAL_PRODUCT_ID &&
    productId !== MASTERCLASS_SUBSCRIPTION_PRODUCT_ID
  ) {
    return null
  }

  // Masterclass products: show subscription button and purchase button
  if (isMasterclass) {
    return (
      <>
        <MasterclassButtons
          productName={productName}
          options={options}
          showOptions={showOptions}
          showEmailNotification={showEmailNotification}
          allOptionsOutOfStock={effectiveAllOutOfStock}
          isPreorder={isPreorder}
          noOptionsAvailable={noOptionsAvailable}
          storefrontCurrencySymbol={storefrontCurrencySymbol}
          selectedOptionId={selectedOptionId}
          onSelectedOptionIdChange={optionId => handleOptionIdChange(optionId)}
          isDisabled={isPending}
          onPurchase={handlePurchaseLecture}
        />
        <AddedToCartModal
          isOpen={isAddedToCartModalOpen}
          productName={addedProductName}
          onClose={() => {
            setIsAddedToCartModalOpen(false)
            setCartNotice(null)
          }}
          isPreorder={isPreorder}
          preorderReleaseDate={preorderReleaseDate}
          notice={cartNotice}
        />
        {/* Purchase lecture modals - only render if they might be needed */}
        {options.length > 1 && (
          <ChooseOptionModal
            isOpen={isChooseOptionModalOpen}
            productName={productName}
            onClose={closeChooseOptionModal}
            options={options}
            selectedOptionId={selectedOptionId}
            quantity={selectedQuantity}
            showOptions={shouldShowOptionsInModal}
            showQuantitySelector={isDeckOfCards}
            isPreorder={isPreorder}
            onOptionSelect={handleOptionSelect}
            onQuantityChange={setSelectedQuantity}
            onAddToCart={handleAddToCartWithOption}
          />
        )}
        {hasDownloads && (
          <AlreadyPurchasedModal
            isOpen={isAlreadyPurchasedModalOpen}
            productName={productName}
            onClose={() => setIsAlreadyPurchasedModalOpen(false)}
          />
        )}
        {showEmailNotification && (
          <>
            <LoginModal
              isLoginOpen={isLoginOpen}
              setIsLoginOpen={setIsLoginOpen}
              message={`Log in to be notified when ${productName} is back in stock`}
              onLoginSuccess={() => setIsEmailNotificationOpen(true)}
            />
            <EmailNotificationModal
              isOpen={isEmailNotificationOpen}
              onClose={() => setIsEmailNotificationOpen(false)}
              productId={productId}
              productName={productName}
              selectedOptionId={
                selectedOptionId ?? defaultOptionId ?? undefined
              }
            />
          </>
        )}
      </>
    )
  }

  const shouldShowEmailCallToAction =
    showEmailNotification && (effectiveAllOutOfStock || noOptionsAvailable)
  const shouldOpenSingleOptionModal =
    showOptions && nonComponentOptions.length === 1 && shouldShowOptionsInModal
  const shouldOpenChooseOptionModal =
    ((options.length > 1 && !showOptions) ||
      shouldOpenSingleOptionModal ||
      isDeckOfCards) &&
    !shouldShowEmailCallToAction
  const getOptionLabel = (option: AddToCartButtonProps['options'][number]) => {
    return formatAddToCartOptionLabel({
      option,
      hostBrand: displayBrand,
      vatPreference,
      isPreorder,
    })
  }

  return (
    <>
      {showOptions && options.length > 1 && (
        <div className="my-2 md:my-3">
          <select
            className="w-full min-h-14 rounded-md border border-gray-300 bg-white px-4 py-3 text-base md:text-lg font-medium text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            aria-label={
              shouldShowEmailCallToAction
                ? 'Select an option to be notified about'
                : 'Select product option'
            }
            onChange={e => handleOptionIdChange(Number(e.target.value))}
            value={selectedOptionId ?? defaultOptionId ?? ''}
          >
            {options
              .filter(option => {
                if (option.id == null) return false
                const productTypeName = option.productType
                return productTypeName?.toLowerCase() !== 'component'
              })
              .map(option => (
                <option
                  key={option.id}
                  value={option.id}
                  className="text-base md:text-lg"
                  disabled={
                    !option.inStock &&
                    !isPreorder &&
                    !shouldShowEmailCallToAction
                  }
                >
                  {getOptionLabel(option)}
                </option>
              ))}
          </select>
        </div>
      )}
      <div className="text-gray-500 text-sm">
        {shouldShowEmailCallToAction && emailNotificationDisplay === 'link' ? (
          <button
            type="button"
            disabled={isPending}
            title={`Get notified when ${productName} is back in stock`}
            onClick={handleAddToCart}
            className="text-sm font-medium text-vi-red underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            Email me when new stock arrives
          </button>
        ) : (
          <Button
            disabled={isPending}
            title={
              shouldShowEmailCallToAction
                ? `Get notified when ${productName} is back in stock`
                : `Add ${productName} to your cart`
            }
            onClick={
              shouldOpenChooseOptionModal ? handleChooseOption : handleAddToCart
            }
            className={className}
            size={buttonSize}
            color={shouldShowEmailCallToAction ? 'blue' : 'green'}
          >
            {children ? (
              <span className="ml-2">{children}</span>
            ) : shouldShowEmailCallToAction ? (
              'Email me when new stock arrives'
            ) : (
              <>
                <ShoppingCartIcon
                  className={
                    buttonSize === 'sm'
                      ? 'w-4 h-4 mr-1.5 shrink-0'
                      : 'w-5 h-5 mr-2 shrink-0'
                  }
                />
                Add to Cart
              </>
            )}
          </Button>
        )}
      </div>
      <AddedToCartModal
        isOpen={isAddedToCartModalOpen}
        productName={addedProductName}
        onClose={() => {
          setIsAddedToCartModalOpen(false)
          setCartNotice(null)
        }}
        isPreorder={isPreorder}
        preorderReleaseDate={preorderReleaseDate}
        notice={cartNotice}
      />
      {/* Only mount modals when they can actually be used */}
      {shouldOpenChooseOptionModal && (
        <ChooseOptionModal
          isOpen={isChooseOptionModalOpen}
          productName={productName}
          onClose={closeChooseOptionModal}
          options={options}
          selectedOptionId={selectedOptionId}
          quantity={selectedQuantity}
          showOptions={shouldShowOptionsInModal}
          showQuantitySelector={isDeckOfCards}
          isPreorder={isPreorder}
          onOptionSelect={handleOptionSelect}
          onQuantityChange={setSelectedQuantity}
          onAddToCart={handleAddToCartWithOption}
        />
      )}
      {hasDownloads && (
        <AlreadyPurchasedModal
          isOpen={isAlreadyPurchasedModalOpen}
          productName={productName}
          onClose={() => setIsAlreadyPurchasedModalOpen(false)}
        />
      )}
      {showEmailNotification && (
        <>
          <LoginModal
            isLoginOpen={isLoginOpen}
            setIsLoginOpen={setIsLoginOpen}
            message={`Log in to be notified when ${productName} is back in stock`}
            onLoginSuccess={() => setIsEmailNotificationOpen(true)}
          />
          <EmailNotificationModal
            isOpen={isEmailNotificationOpen}
            onClose={() => setIsEmailNotificationOpen(false)}
            productId={productId}
            productName={productName}
            selectedOptionId={selectedOptionId ?? defaultOptionId ?? undefined}
          />
        </>
      )}
    </>
  )
}

export function AddToCartButton(props: AddToCartButtonProps) {
  const customFlowSlug = getFlowSlugForProductId(props.productId)
  const { configureFlowLinkClassName, ...innerProps } = props
  const customFlowIsPurchasable =
    props.isPreorder ||
    props.productInStock ||
    props.options.some(option => option.inStock)

  if (customFlowSlug && customFlowIsPurchasable) {
    return (
      <Link
        href={`/add-to-cart/${customFlowSlug}?productId=${props.productId}`}
        className={configureFlowLinkClassName ?? 'block w-full'}
      >
        <Button
          color="green"
          className={props.className}
          size={props.buttonSize ?? 'lg'}
        >
          {props.children ?? 'Configure & add to cart'}
        </Button>
      </Link>
    )
  }
  return <AddToCartButtonInner {...innerProps} />
}

interface AddToCartButtonProps {
  productId: number
  productName: string
  className?: string
  /** Visual size for the primary CTA button. Defaults to `lg` (e.g. product pages). */
  buttonSize?: 'sm' | 'md' | 'lg'
  /** Applied to the wrapping `Link` when this product uses a custom add-to-cart flow. */
  configureFlowLinkClassName?: string
  options: {
    id: number
    description: string
    price: number
    salePrice?: number | null
    priceIncVat?: number | null
    salePriceIncVat?: number | null
    taxType?: number | null
    currencySymbol: string
    currencyCode?: string | null
    inStock: boolean
    productType: string
    needsShipping: boolean
  }[]
  productInStock?: boolean
  children?: React.ReactNode
  showOptions?: boolean
  showEmailNotification?: boolean
  emailNotificationDisplay?: 'button' | 'link'
  isMasterclass?: boolean
  isDeckOfCards?: boolean
  hasDownloads?: boolean
  ebookBundleOption?: {
    physicalOptionId: number
    downloadOptionId: number
  } | null
  isPreorder?: boolean
  preorderReleaseDate?: string | null
  /** Parent PDP: mixed child stock — add in-stock children only (legacy AjaxAdd). */
  groupChildProducts?: GroupChildProductForCart[] | null
  /**
   * When true, option dropdown state is not synced to the global PDP store.
   * Use for secondary product rows on the same page (e.g. parent + child list).
   */
  independentOptionSelection?: boolean
  brandCode?: RegionalBrandCode | null
}

export { FreeDownloadButton } from './FreeDownloadButton'
